import { router } from './index';

// 控制台 Views
import Layout from '@/layouts/Layout.vue';
import Dashboard from '@/views/Dashboard.vue';
import ConsultantScheduleCalendar from '@/views/consultation/ConsultantScheduleCalendar.vue';
import { DashboardOutlined, CalendarOutlined } from '@ant-design/icons-vue';
import { getPagePermissions } from '@/constants/permissions';

// 權限API
import { getMyPermissions } from '@/api/assign-role/getMyPermissions';

// 工具函數
import { filterRoutesByPermissions as filterRoutes } from '@/utils/routeFilter';
import { modulesConfig } from './modulesConfig';
import { resolveDefaultHomePathFromAdmin, getAdminFromStorage } from '@/utils/consultantHome';

let hasLoadedPrivateRoutes = false;

export let dynamicRoutes = [];

/** 動態路由的固定名稱，清除時用來 removeRoute */
const ROUTE_NAME_PRIVATE_LAYOUT = 'PrivateLayout';
const ROUTE_NAME_PRIVATE_CATCH_ALL = 'PrivateCatchAll';

/**
 * 將有 children 的模組攤平為一層：每個子路由變成 Layout 的直接子路由，path 用相對路徑（無前綴 /）。
 * @param {Array} matchedRoutes - filterRoutes 回傳的模組陣列（可能含 children）
 * @returns {Array} 攤平後的路由設定，每項為 { path, component, name, meta }
 */
function flattenRoutesForLayout(matchedRoutes) {
    const flat = [];
    matchedRoutes.filter(Boolean).forEach((module) => {
        const parentPath = (module.path || '').replace(/^\//, '');
        if (!module.children || module.children.length === 0) {
            flat.push({
                path: parentPath || 'dashboard',
                component: module.component,
                name: module.name,
                meta: module.meta || {}
            });
            return;
        }
        module.children.forEach((child) => {
            const childPath = child.path != null && child.path !== '' ? String(child.path) : '';
            const fullPath = childPath ? `${parentPath}/${childPath}` : parentPath;
            if (child.redirect) {
                flat.push({
                    path: fullPath,
                    redirect: child.redirect,
                    name: child.name,
                    meta: child.meta || {}
                });
                return;
            }
            if (!child.component) {
                return;
            }
            flat.push({
                path: fullPath,
                component: child.component,
                name: child.name,
                meta: child.meta || {}
            });
        });
    });
    return flat;
}

/**
 * 註冊動態路由（攤平後全部作為 Layout 的直接子路由，確保子頁一定會掛載並發 API）
 * @param {Array} matchedRoutes - 匹配的路由清單（filterRoutes 回傳）
 */
function resolveDefaultHomePath() {
    return resolveDefaultHomePathFromAdmin(getAdminFromStorage());
}

function registerDynamicRoutes(matchedRoutes) {
    if (dynamicRoutes.length > 0) {
        router.removeRoute(ROUTE_NAME_PRIVATE_CATCH_ALL);
        router.removeRoute(ROUTE_NAME_PRIVATE_LAYOUT);
    }

    const routesToRegister = flattenRoutesForLayout(matchedRoutes);
    const homePath = resolveDefaultHomePath();

    dynamicRoutes = [
        {
            name: ROUTE_NAME_PRIVATE_LAYOUT,
            path: '/',
            component: Layout,
            redirect: homePath,
            children: [
                {
                    path: 'dashboard',
                    component: Dashboard,
                    name: 'Dashboard',
                    meta: {
                        title: '儀表板',
                        icon: DashboardOutlined,
                        hidePageChrome: true
                    }
                },
                {
                    path: 'schedule-calendar',
                    component: ConsultantScheduleCalendar,
                    name: 'ConsultantScheduleCalendar',
                    meta: {
                        title: '行事曆',
                        icon: CalendarOutlined,
                        permissionIds: [
                            getPagePermissions('ConsultantScheduleCalendar')?.view,
                            getPagePermissions('ConsultantScheduleCalendar')
                                ?.viewFallback,
                            getPagePermissions('Consultants')?.view
                        ].filter(Boolean)
                    }
                },
                ...routesToRegister
            ]
        },
        {
            name: ROUTE_NAME_PRIVATE_CATCH_ALL,
            path: '/:pathMatch(.*)*',
            redirect: homePath
        }
    ];

    dynamicRoutes.forEach((route) => {
        router.addRoute(route);
    });
}

/** 選單權限有增減時遞增，讓已登入的瀏覽器捨棄舊的 userPermissions 快取並重抓 */
const PERMISSION_CACHE_REVISION = '2026-10-21-operation-logs-v2';

function readCachedPermissions() {
    try {
        if (localStorage.getItem('userPermissionsRevision') !== PERMISSION_CACHE_REVISION) {
            localStorage.removeItem('userPermissions');
            return null;
        }
        const raw = localStorage.getItem('userPermissions');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch (error) {
        console.error('讀取快取權限失敗:', error);
        return null;
    }
}

function persistPermissions(permissions) {
    try {
        localStorage.setItem('userPermissionsRevision', PERMISSION_CACHE_REVISION);
        localStorage.setItem('userPermissions', JSON.stringify(permissions));
    } catch (error) {
        console.error('儲存使用者權限資料失敗:', error);
    }
}

function applyPermissions(permissions) {
    const matchedRoutes = filterRoutes(permissions, modulesConfig);
    registerDynamicRoutes(matchedRoutes);
    hasLoadedPrivateRoutes = true;
}

/**
 * 注入私有路由（依權限過濾後註冊）。
 * 注意：修改 router/routes/* 後需「整頁重新整理」或重新登入才會套用新路由，因注入只在進入時執行一次。
 *
 * @param {boolean} forceReload - 是否強制重新載入
 */
export async function injectPrivateRoutesByPermission(forceReload = false) {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
        console.log('未找到存取權杖，跳過路由注入');
        return;
    }

    // 如果已經載入過且不是強制重新載入，直接返回
    if (hasLoadedPrivateRoutes && !forceReload) {
        console.log('路由已載入，跳過重複載入');
        return;
    }

    // 如果是強制重新載入，重置狀態
    if (forceReload) {
        hasLoadedPrivateRoutes = false;
        dynamicRoutes = [];
        console.log('強制重新載入路由');
    }

    // 新分頁／重整：優先用本機快取，避免再打 permissions 撞上 429
    if (!forceReload) {
        const cached = readCachedPermissions();
        if (cached) {
            applyPermissions(cached);
            return;
        }
    }

    try {
        const response = await getMyPermissions();

        if (response.status !== 1) {
            console.error('權限 API 回應狀態異常:', response);
            const cached = readCachedPermissions();
            if (cached) {
                applyPermissions(cached);
                return;
            }
            return;
        }

        const permissions = response.data;
        persistPermissions(permissions);
        applyPermissions(permissions);
    } catch (error) {
        console.error('初始化路由失敗:', error);
        const cached = readCachedPermissions();
        if (cached) {
            applyPermissions(cached);
            return;
        }
        throw error;
    }
}

/**
 * 清除動態路由
 */
export function clearDynamicRoutes() {
    if (dynamicRoutes.length > 0) {
        router.removeRoute(ROUTE_NAME_PRIVATE_CATCH_ALL);
        router.removeRoute(ROUTE_NAME_PRIVATE_LAYOUT);
    }
    dynamicRoutes = [];
    hasLoadedPrivateRoutes = false;

    // 清除權限資料
    try {
        localStorage.removeItem('userPermissions');
    } catch (error) {
        console.error('清除使用者權限資料失敗:', error);
    }

    console.log('已清除動態路由和權限資料');
}

/**
 * 重新載入路由
 */
export async function reloadRoutes() {
    await injectPrivateRoutesByPermission(true);
}
